import { Injectable } from '@angular/core';
import { CompanyControllerService } from '@iota/iota-management-portal-v4-client';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, concat, forkJoin, map, mergeMap, of, switchMap, take, withLatestFrom } from 'rxjs';
import { getLocationHeader } from 'src/app/shared/utils';
import {
    AddUpdateRemoveCompanyProductSelection,
    AddUpdateRemoveCompanyProductSelectionAttributes,
    CompanyProductSelectionActionTypes,
    LoadCompanyProductSelectionsByCompany,
    LoadCompanyProductSelectionsFailure,
    LoadCompanyProductSelectionsSuccess,
    ReloadCompanyProductSelectionsByIds,
    SaveLatestCompanyProductSelectionIdsAndLoadCompanyProductSelectionsByCompany
} from '../actions/company-product-selection.actions';
import { LoadProductsByIds, ProductActionTypes } from '../actions/product.actions';
import { selectedCompanyId } from '../store';

@Injectable()
export class CompanyProductSelectionEffects {
    loadProductSelectionsByCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadCompanyProductSelectionsByCompany),
            switchMap(({ companyId }) =>
                this.apiService.getAllCompanyProductSelectionsByCompany(companyId).pipe(
                    switchMap((productSelections) => [
                        LoadCompanyProductSelectionsSuccess({ productSelections: productSelections ?? [], companyId }),
                        LoadProductsByIds({ productIds: productSelections?.map((selection) => selection.infinkonProductId) ?? [] })
                    ]),
                    catchError((error) => of(LoadCompanyProductSelectionsFailure({ error })))
                )
            )
        )
    );

    reloadCompanyProductSelectionsByIds$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReloadCompanyProductSelectionsByIds),
            mergeMap(({ companyProductSelectionIds }) =>
                this.apiService.getAllCompanyProductSelectionsByIds(companyProductSelectionIds).pipe(
                    take(1),
                    switchMap((productSelections) => [
                        {
                            type: CompanyProductSelectionActionTypes.ReloadCompanyProductSelectionsByIdsSuccess,
                            productSelections
                        },
                        {
                            type: ProductActionTypes.LoadProductsByIds,
                            productIds: productSelections.map((selection) => selection.infinkonProductId)
                        }
                    ]),
                    catchError((error) => of({ type: CompanyProductSelectionActionTypes.LoadCompanyProductSelectionsFailure, error }))
                )
            )
        )
    );

    addUpdateRemoveCompanyProductSelections$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AddUpdateRemoveCompanyProductSelection),
            withLatestFrom(this.store.select(selectedCompanyId)),
            mergeMap(([{ upsertedProductSelections, removedProductSelections }, companyId]) =>
                concat(
                    forkJoin([
                        ...(upsertedProductSelections?.map((added) => this.apiService.upsertCompanyProductSelection(added, 'response')) ??
                            [])
                    ]),
                    forkJoin([
                        ...(removedProductSelections?.map((removed) =>
                            this.apiService.removeCompanyProductSelection(removed, 'response')
                        ) ?? [])
                    ])
                ).pipe(
                    take(1),
                    map((res) => {
                        const allLocationHeaders = res?.map((response) => getLocationHeader(response));
                        const allExistingLocationHeaders = allLocationHeaders?.filter((header) => !!header);

                        return SaveLatestCompanyProductSelectionIdsAndLoadCompanyProductSelectionsByCompany({
                            companyId,
                            latestCompanyProductSelectionIds: allExistingLocationHeaders
                        });
                    }),
                    catchError((error) => of({ type: CompanyProductSelectionActionTypes.CompanyProductSelectionActionFailure, error }))
                )
            )
        )
    );

    saveLatestCompanyProductSelectionIdsAndLoadCompanyProductSelectionsByCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SaveLatestCompanyProductSelectionIdsAndLoadCompanyProductSelectionsByCompany),
            map(({ companyId }) => LoadCompanyProductSelectionsByCompany({ companyId }))
        )
    );

    addUpdateRemoveCompanyProductSelectionAttributes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AddUpdateRemoveCompanyProductSelectionAttributes),
            mergeMap(({ attributeResult, companyProductSelectionId }) =>
                forkJoin([
                    attributeResult.upsertedAttributes?.length > 0
                        ? this.apiService.upsertCompanyProductSelectionAttributes(
                              companyProductSelectionId,
                              attributeResult.upsertedAttributes
                          )
                        : of(null),
                    attributeResult.removedAttributeIds?.length > 0
                        ? this.apiService.removeCompanyProductSelectionAttributes(attributeResult.removedAttributeIds)
                        : of(null)
                ]).pipe(
                    take(1),
                    map(() => ({
                        type: CompanyProductSelectionActionTypes.ReloadCompanyProductSelectionsByIds,
                        companyProductSelectionIds: [companyProductSelectionId]
                    })),
                    catchError((error) =>
                        of({ type: CompanyProductSelectionActionTypes.CompanyProductSelectionActionFailure, error: error })
                    )
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private store: Store,
        private apiService: CompanyControllerService
    ) {}
}
